import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NewBadge, Cover, MediaLoadingSpinner } from '../primitives';
import { localizedField } from '../../locales/content';
import { hapticImpact } from '../../utils/haptics';
import { getCardTitleImageWidth } from './titleImageSizing';

const isVideoSource = (url) =>
  typeof url === 'string' && /\.(mp4|webm|mov)(\?.*)?$/i.test(url);

/**
 * VideoPresetCard — карточка видео-пресета в двухколоночной сетке каталога.
 *
 * Figma source: `generation` component в node `318:4727` (gallery screen).
 * Aspect ratio 3:4 (handoff §7).
 *
 * Props:
 *   preset  — { id, name, coverUrl, previewClipUrl?, isNew }
 *   status? — 'idle' | 'generating' | 'error'  (default: 'idle')
 *
 * При status='error'     — overlay «Ошибка\nгенерации» (node 40000032:6420).
 * При status='generating' — overlay с loader + «Генерируем...» (gallery.png
 *                           карточка #4).
 * Tap → navigate('/video-generate/' + preset.id)  ([05.4] route).
 *
 * Media priority (acceptance §2.1 + 05b §2):
 *   previewClipUrl (.mp4 / .webm)  → <video autoPlay loop muted playsInline>
 *   coverUrl                       → <Cover> primitive (image fallback)
 *   Если preview ресурс 404 — onError переключаемся на coverUrl.
 */
export default function VideoPresetCard({ preset, status = 'idle', cardSize = 'M' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const intersectsViewportRef = useRef(false);
  const [previewBroken, setPreviewBroken] = useState(false);
  const name = localizedField(preset, 'name') || preset.name;
  const titleImage = preset?.titleImage || preset?.logoUrl || null;

  const handleTap = () => {
    hapticImpact('light');
    navigate(`/video-generate/${preset.id}`);
  };

  const previewSource = !previewBroken && isVideoSource(preset.previewClipUrl)
    ? preset.previewClipUrl
    : null;
  const [isPreviewVisible, setIsPreviewVisible] = useState(
    () => !previewSource || typeof IntersectionObserver === 'undefined'
  );
  const previewUrl = isPreviewVisible ? previewSource : null;
  const [mediaLoaded, setMediaLoaded] = useState(() => !previewUrl);

  useEffect(() => {
    if (!previewSource || typeof IntersectionObserver === 'undefined') {
      setIsPreviewVisible(true);
      return undefined;
    }

    const card = cardRef.current;
    if (!card) return undefined;

    intersectsViewportRef.current = false;
    setIsPreviewVisible(false);

    const syncVisibility = () => {
      const isVisible =
        intersectsViewportRef.current && document.visibilityState !== 'hidden';
      if (!isVisible) videoRef.current?.pause();
      setIsPreviewVisible(isVisible);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        intersectsViewportRef.current = Boolean(entry?.isIntersecting);
        syncVisibility();
      },
      { root: null, rootMargin: '0px', threshold: 0.01 }
    );
    const handleDocumentVisibility = () => syncVisibility();

    observer.observe(card);
    document.addEventListener('visibilitychange', handleDocumentVisibility);

    return () => {
      videoRef.current?.pause();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleDocumentVisibility);
    };
  }, [previewSource]);

  useEffect(() => {
    setMediaLoaded(!previewUrl);
  }, [previewUrl]);

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={handleTap}
      className="relative w-full rounded-home-16 overflow-hidden bg-surface-2"
      style={{ aspectRatio: '3 / 4' }}
      aria-label={name || 'Video preset'}
    >
      {/* Media: video preview or cover image via Cover primitive */}
      {previewUrl ? (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          src={previewUrl}
          poster={preset.coverUrl || undefined}
          onLoadedData={() => setMediaLoaded(true)}
          onError={() => {
            setMediaLoaded(true);
            setPreviewBroken(true);
          }}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="video-preview"
        />
      ) : preset.coverUrl ? (
        <Cover
          coverUrl={preset.coverUrl}
          alt={name || ''}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 0 }}
          className="!w-full !h-full !rounded-none"
        />
      ) : (
        <div className="absolute inset-0 bg-surface-2" />
      )}
      {previewUrl && !mediaLoaded && <MediaLoadingSpinner />}

      {/* NEW badge — позиционируем напрямую через className на самом
          primitive (он `<span>`, forwards-className). Лишний `<div>`-wrapper
          уехал: inline-flex span внутри block-div ловил baseline-offset от
          line-height родителя и визуально отскакивал от верха. TR-угол = 16
          совпадает с `rounded-home-16` родителя, чтобы badge прильнул к
          скруглению карточки без клип-зазора. Figma `363:4576`. */}
      {preset.isNew && (
        <NewBadge coverRadius={16} className="absolute top-0 right-0 z-10" />
      )}
      {titleImage && (
        <img
          src={titleImage}
          alt=""
          className="absolute left-1/2 bottom-4 z-[2] h-auto max-w-full -translate-x-1/2 object-contain pointer-events-none"
          style={{ width: getCardTitleImageWidth(cardSize) }}
          loading="lazy"
          draggable={false}
        />
      )}
      {!titleImage && name && (
        <span className="absolute inset-x-3 bottom-4 z-[2] text-left font-sans text-chip-label font-semibold text-text-primary pointer-events-none">
          {name}
        </span>
      )}

      {/* Generating overlay */}
      {status === 'generating' && (
        <div
          data-testid="generating-overlay"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
          /* figma:ref rgba(1,1,3,0.72) overlay — no token yet, inline ok */
          style={{ background: 'rgba(1,1,3,0.72)' }}
        >
          {/* Spinner */}
          <div
            className="w-6 h-6 rounded-full border-2 border-surface-4 border-t-icon-accent animate-spin"
            aria-hidden
          />
          <span className="font-sans text-chip-label text-text-primary">
            {t('gallery.generating')}...
          </span>
        </div>
      )}

      {/* Error overlay — figma node 40000032:6420 */}
      {status === 'error' && (
        <div
          data-testid="error-overlay"
          className="absolute inset-0 flex items-center justify-center"
          /* figma:ref rgba(1,1,3,0.72) overlay — no token yet, inline ok */
          style={{ background: 'rgba(1,1,3,0.72)' }}
        >
          <span className="font-sans text-chip-label text-text-primary text-center whitespace-pre-line">
            {t('gallery.generationError').replace(' ', '\n')}
          </span>
        </div>
      )}
    </button>
  );
}
